import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Upload, Image as ImageIcon, FileText, ExternalLink,
  User, Phone, Share2, Palette, Loader2, Power,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { ImageCropper } from "@/components/ImageCropper";
import { StatusBadge } from "@/components/dashboard/DashBits";
import { WalletButtons } from "@/components/dashboard/WalletButtons";

const Field = ({ label, value, onChange, placeholder, type = "text", testid }) => (
  <div>
    <label className="text-xs text-zinc-500 mb-1 block">{label}</label>
    <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
      className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-white text-sm placeholder:text-zinc-600 focus-ring" />
  </div>
);

export default function ProfileEditor() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const isAdmin = user.role === "superadmin";
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("infos");
  const [saving, setSaving] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropTarget, setCropTarget] = useState(null);
  const [pdfUploading, setPdfUploading] = useState(false);

  useEffect(() => {
    api.get(`/company/profiles/${id}`).then(({ data }) => { setProfile(data); setData(data.data); })
      .catch(() => { toast.error(t("dash.profileNotFound")); navigate("/dashboard/profils"); });
  }, [id, navigate]);

  const set = (k) => (v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const base = isAdmin ? "/admin/profiles" : "/company/profiles";
      await api.put(`${base}/${id}`, { data });
      toast.success(t("dash.saved"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    const base = isAdmin ? "/admin/profiles" : "/company/profiles";
    try {
      const { data: res } = await api.patch(`${base}/${id}/toggle`);
      setProfile((p) => ({ ...p, active: res.active }));
      toast.success(t("dash.statusUpdated"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const onFile = (target, aspect) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCropTarget({ target, aspect });
    setCropSrc(URL.createObjectURL(file));
    e.target.value = "";
  };

  const onPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPdfUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: res } = await api.post("/files/upload?kind=pdf", form, { headers: { "Content-Type": "multipart/form-data" } });
      set("pdf_url")(res.path);
      toast.success(t("dash.pdfAdded"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPdfUploading(false);
      e.target.value = "";
    }
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" /></div>;

  const TABS = [
    { k: "infos", l: t("dash.tab_infos"), icon: User }, { k: "contact", l: t("dash.tab_contact"), icon: Phone },
    { k: "reseaux", l: t("dash.tab_social"), icon: Share2 }, { k: "design", l: t("dash.tab_design"), icon: Palette },
  ];

  return (
    <div className="pb-24">
      <button onClick={() => navigate("/dashboard/profils")} className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors duration-300">
        <ArrowLeft className="w-4 h-4" /> {t("dash.backProfiles")}
      </button>

      <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-mono text-sm text-cyan-400">{profile.code}</span>
          <h1 className="font-display font-bold text-2xl text-white">{`${data.first_name || ""} ${data.last_name || ""}`.trim() || t("dash.newProfileTitle")}</h1>
          <StatusBadge status={profile.active ? "active" : "inactive"} />
        </div>
        <div className="flex items-center gap-2">
          <a href={`/${profile.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 glass text-white px-3.5 py-2 rounded-full text-sm"><ExternalLink className="w-4 h-4" /> {t("dash.view")}</a>
          <button onClick={toggleActive} data-testid="editor-toggle" className={`inline-flex items-center gap-2 glass px-3.5 py-2 rounded-full text-sm ${profile.active ? "text-emerald-400" : "text-zinc-400"}`}><Power className="w-4 h-4" /> {profile.active ? t("dash.active") : t("dash.inactive")}</button>
          <button onClick={save} disabled={saving} data-testid="editor-save" className="inline-flex items-center gap-2 bg-brand-gradient text-white font-semibold px-5 py-2 rounded-full disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t("dash.save")}
          </button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {TABS.map((tb) => (
          <button key={tb.k} onClick={() => setTab(tb.k)} data-testid={`editor-tab-${tb.k}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors duration-300 ${tab === tb.k ? "bg-brand-gradient text-white" : "glass text-zinc-400 hover:text-white"}`}>
            <tb.icon className="w-4 h-4" /> {tb.l}
          </button>
        ))}
      </div>

      <div className="mt-6 rounded-2xl border border-white/10 bg-[#0d0d14] p-6">
        {tab === "infos" && (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="editor-infos">
            <Field label={t("dash.f_first")} value={data.first_name} onChange={set("first_name")} testid="ed-first-name" />
            <Field label={t("dash.f_last")} value={data.last_name} onChange={set("last_name")} testid="ed-last-name" />
            <Field label={t("dash.f_job")} value={data.job_title} onChange={set("job_title")} testid="ed-job" />
            <Field label={t("dash.f_companyName")} value={data.company_name} onChange={set("company_name")} testid="ed-company" />
            <div className="sm:col-span-2">
              <label className="text-xs text-zinc-500 mb-1 block">{t("dash.f_desc")}</label>
              <textarea rows={3} value={data.description || ""} onChange={(e) => set("description")(e.target.value)} data-testid="ed-description"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-white text-sm focus-ring" />
            </div>
          </div>
        )}

        {tab === "contact" && (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="editor-contact">
            <Field label={t("dash.f_phone")} value={data.phone} onChange={set("phone")} testid="ed-phone" />
            <Field label={t("dash.f_phone2")} value={data.phone2} onChange={set("phone2")} testid="ed-phone2" />
            <Field label={t("dash.f_email")} value={data.email} onChange={set("email")} type="email" testid="ed-email" />
            <Field label={t("dash.f_email2")} value={data.email2} onChange={set("email2")} type="email" testid="ed-email2" />
            <Field label={t("dash.f_web")} value={data.website} onChange={set("website")} placeholder="https://…" testid="ed-website" />
            <Field label={t("dash.f_web2")} value={data.website2} onChange={set("website2")} placeholder="https://…" testid="ed-website2" />
            <div className="sm:col-span-2"><Field label={t("dash.f_address")} value={data.address} onChange={set("address")} testid="ed-address" /></div>
          </div>
        )}

        {tab === "reseaux" && (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="editor-reseaux">
            <Field label="LinkedIn" value={data.linkedin} onChange={set("linkedin")} placeholder="https://linkedin.com/in/…" testid="ed-linkedin" />
            <Field label={t("dash.f_whatsapp")} value={data.whatsapp} onChange={set("whatsapp")} placeholder="+33…" testid="ed-whatsapp" />
            <Field label="Instagram" value={data.instagram} onChange={set("instagram")} testid="ed-instagram" />
            <Field label="Facebook" value={data.facebook} onChange={set("facebook")} testid="ed-facebook" />
            <Field label="TikTok" value={data.tiktok} onChange={set("tiktok")} testid="ed-tiktok" />
            <Field label="YouTube" value={data.youtube} onChange={set("youtube")} testid="ed-youtube" />
            <Field label={t("dash.f_google")} value={data.google} onChange={set("google")} testid="ed-google" />
            <Field label={t("dash.f_reviews")} value={data.google_reviews} onChange={set("google_reviews")} testid="ed-reviews" />
            <div className="sm:col-span-2">
              <label className="text-xs text-zinc-500 mb-1 block">{t("dash.f_pdf")}</label>
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2 glass px-4 py-2.5 rounded-xl text-sm text-white cursor-pointer hover:bg-white/10 transition-colors duration-300" data-testid="ed-pdf-upload">
                  {pdfUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} {t("dash.choosePdf")}
                  <input type="file" accept="application/pdf" className="hidden" onChange={onPdf} />
                </label>
                {data.pdf_url && <><a href={fileUrl(data.pdf_url)} target="_blank" rel="noreferrer" className="text-sm text-cyan-400">{t("dash.seePdf")}</a><button onClick={() => set("pdf_url")("")} className="text-sm text-red-400">{t("dash.removePdf")}</button></>}
              </div>
            </div>
          </div>
        )}

        {tab === "design" && (
          <div className="space-y-6" data-testid="editor-design">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-xs text-zinc-500 mb-2 block">{t("dash.profilePhoto")}</label>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center">
                    {data.profile_photo_url ? <img src={fileUrl(data.profile_photo_url)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-zinc-600" />}
                  </div>
                  <label className="inline-flex items-center gap-2 glass px-4 py-2.5 rounded-xl text-sm text-white cursor-pointer hover:bg-white/10 transition-colors duration-300" data-testid="ed-photo-upload">
                    <Upload className="w-4 h-4" /> {t("dash.import")}
                    <input type="file" accept="image/*" className="hidden" onChange={onFile("profile_photo_url", 1)} />
                  </label>
                </div>
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-2 block">{t("dash.coverImage")}</label>
                <div className="flex items-center gap-4">
                  <div className="w-28 h-16 rounded-xl overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center">
                    {data.cover_url ? <img src={fileUrl(data.cover_url)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-zinc-600" />}
                  </div>
                  <label className="inline-flex items-center gap-2 glass px-4 py-2.5 rounded-xl text-sm text-white cursor-pointer hover:bg-white/10 transition-colors duration-300" data-testid="ed-cover-upload">
                    <Upload className="w-4 h-4" /> {t("dash.import")}
                    <input type="file" accept="image/*" className="hidden" onChange={onFile("cover_url", 16 / 9)} />
                  </label>
                </div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-xs text-zinc-500 mb-2 block">{t("dash.primaryColor")}</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={data.primary_color || "#06B6D4"} onChange={(e) => set("primary_color")(e.target.value)} data-testid="ed-primary-color" className="w-12 h-12 rounded-lg bg-transparent cursor-pointer" />
                  <span className="font-mono text-sm text-zinc-400">{data.primary_color}</span>
                </div>
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-2 block">{t("dash.borderColor")}</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={data.border_color || "#2563EB"} onChange={(e) => set("border_color")(e.target.value)} data-testid="ed-border-color" className="w-12 h-12 rounded-lg bg-transparent cursor-pointer" />
                  <span className="font-mono text-sm text-zinc-400">{data.border_color}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {!isAdmin && <WalletButtons profileId={id} />}

      {cropSrc && (
        <ImageCropper src={cropSrc} aspect={cropTarget.aspect} onCancel={() => setCropSrc(null)}
          onDone={(res) => { set(cropTarget.target)(res.path); setCropSrc(null); }} />
      )}
    </div>
  );
}
